// API client utility for making requests to the backend
import type { ApiError } from "@wtf/shared";

// Get API base URL from environment or default
const API_BASE_URL = (import.meta as any).env?.VITE_API_URL || "http://localhost:3000";

export type RequestMethod = "GET" | "POST" | "PUT" | "DELETE" | "PATCH";

/**
 * Optional monitoring/observability hooks for API client
 * Allows platforms to inject Sentry, logging, or other monitoring
 */
export interface ApiClientOptions {
  captureException?: (error: Error, context?: any) => void;
  addBreadcrumb?: (message: string, category: string, data?: any) => void;
}

let clientOptions: ApiClientOptions = {};

/**
 * Configure API client with optional monitoring/observability
 * Call this during app initialization to enable error tracking
 */
export function configureApiClient(options: ApiClientOptions) {
  clientOptions = options;
}

/**
 * Convert an object of query parameters to a URL query string
 */
export function objectToQueryString(params: Record<string, any>): string {
  if (!params || Object.keys(params).length === 0) {
    return "";
  }

  const queryParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      if (typeof value === "boolean") {
        queryParams.append(key, value.toString());
      } else {
        queryParams.append(key, String(value));
      }
    }
  });

  const queryString = queryParams.toString();
  return queryString ? `?${queryString}` : "";
}

/**
 * Get Firebase ID token for authenticated requests
 * Must be implemented by the platform (PWA or Extension)
 */
type GetIdTokenFn = () => Promise<string | null>;
type WaitForAuthInitFn = () => Promise<any>;

let getIdTokenImpl: GetIdTokenFn | null = null;
let waitForAuthInitImpl: WaitForAuthInitFn | null = null;

/**
 * Configure Firebase auth functions
 * Must be called during app initialization
 */
export function configureAuth(config: { getIdToken: GetIdTokenFn; waitForAuthInit: WaitForAuthInitFn }) {
  getIdTokenImpl = config.getIdToken;
  waitForAuthInitImpl = config.waitForAuthInit;
}

/**
 * Main API client function for making HTTP requests
 */
export async function apiClient<T>(
  endpoint: string,
  method: RequestMethod = "GET",
  data?: any,
  options: RequestInit = {}
): Promise<T> {
  if (!getIdTokenImpl || !waitForAuthInitImpl) {
    throw new Error("API client not configured. Call configureAuth() during app initialization.");
  }

  // Ensure endpoint starts with a slash
  const formattedEndpoint = endpoint.startsWith("/") ? endpoint : `/${endpoint}`;
  const url = `${API_BASE_URL}${formattedEndpoint}`;

  // Wait for Firebase Auth to initialize before getting the token
  await waitForAuthInitImpl();
  const token = await getIdTokenImpl();

  // Prepare headers with proper typing
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...((options.headers as Record<string, string>) || {}),
  };

  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  // Prepare request config
  const config: RequestInit = {
    method,
    headers,
    ...options,
    body: data && method !== "GET" ? JSON.stringify(data) : undefined,
  };

  try {
    // Optional breadcrumb for debugging
    clientOptions.addBreadcrumb?.(`API ${method} ${formattedEndpoint}`, "api-request", {
      endpoint: formattedEndpoint,
      method,
      hasAuth: !!token,
    });

    const response = await fetch(url, config);

    // Handle HTTP errors
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));

      const error = new Error(errorData.message || `API error: ${response.status}`) as ApiError;
      error.statusCode = response.status;
      error.errorCode = errorData.errorCode;
      error.retryable = errorData.retryable ?? false;
      error.retryAfter = errorData.retryAfter;
      error.details = errorData.details;

      // Optional error capture
      clientOptions.captureException?.(error, {
        endpoint: formattedEndpoint,
        method,
        statusCode: response.status,
        errorCode: errorData.errorCode,
        errorData,
      });

      throw error;
    }

    // Handle 204 No Content response
    if (response.status === 204) {
      return {} as T;
    }

    // Parse JSON response
    const result = await response.json();
    return result as T;
  } catch (error) {
    // Only log to console and capture if not already handled above
    if (!(error as ApiError).statusCode) {
      console.error(`API request failed: ${url}`, error);

      // Optional error capture for network/parsing errors
      clientOptions.captureException?.(error as Error, {
        endpoint: formattedEndpoint,
        method,
        url,
        type: "network_or_parse_error",
      });
    }

    throw error;
  }
}
